const COMBINATOR_KEYWORDS = new Set(['oneOf', 'anyOf', 'allOf']);

/**
 * Converts a JSON Schema property path to a JSONPath expression.
 *
 * Schema paths produced by `findSchemaPathsWhere` follow a strict grammar:
 *
 *  - `properties.<name>` → an object property (the token after `properties` is
 *    always the data key, even when that key is itself called "properties" or
 *    "items")
 *  - `items` (standalone) → array wildcard `[*]`
 *  - `oneOf | anyOf | allOf` → structural combinator, skipped
 *
 * @param schemaPath e.g. `"properties.images.items.properties.url"`
 * @returns JSONPath  e.g. `"$.images[*].url"`
 */
export const convertSchemaPathToJsonPath = (schemaPath: string): string => {
  const parts = schemaPath.split('.');

  // `skipNext` is set after we encounter the `properties` schema keyword so
  // the very next token is consumed as a data key without any keyword checks.
  let skipNext = false;

  const jsonParts = parts.reduce<string[]>((acc, part) => {
    if (skipNext) {
      // This token is the data key that follows `properties` — always keep it
      acc.push(part);
      skipNext = false;
    } else if (part === 'properties') {
      // Schema keyword — consume the next token as the data key
      skipNext = true;
    } else if (part === 'items') {
      acc.push('[*]');
    } else if (!COMBINATOR_KEYWORDS.has(part)) {
      acc.push(part);
    }
    return acc;
  }, []);

  return jsonParts.reduce(
    (path, part) => path + (part === '[*]' ? part : `.${part}`),
    '$',
  );
};
